// src/components/PreviousHandoff.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronUp, FileText, Mic, User, Clock, Globe, Loader2 } from 'lucide-react';
import './PreviousHandoff.css';
import { useTheme } from '../contexts/ThemeContext';

// Supported languages for translation
const SUPPORTED_LANGUAGES: Record<string, string> = {
  'en': 'English',
  'es': 'Spanish', 
  'fr': 'French',
  'de': 'German',
  'hi': 'Hindi',
  'zh': 'Chinese',
  'ja': 'Japanese',
  'ko': 'Korean',
  'pt': 'Portuguese',
  'it': 'Italian',
  'ru': 'Russian',
  'ar': 'Arabic',
  'nl': 'Dutch',
  'pl': 'Polish',
  'tr': 'Turkish',
  'sv': 'Swedish',
  'da': 'Danish',
  'no': 'Norwegian',
  'fi': 'Finnish'
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

interface Handoff {
  nurse_name: string;
  nurse_id: string;
  patient_name: string;
  patient_mrn: string;
  handoff_text: string;
  timestamp: string;
  handoff_type?: string;
}

interface PreviousHandoffProps {
  patientMRN: string;
  currentNurseId: string;
  department?: string;
}

// Helper function to convert markdown bold (**text**) to HTML <strong> tags
const convertMarkdownBold = (text: string): string => {
  return text.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
};

// Helper function to split handoff text into narrative and categorical breakdown
const splitHandoffContent = (text: string): { narrative: string; breakdown: string } => {
  // Comprehensive emoji list for all departments
  const emojis = [
    '🫀', '🫁', '🧠', '💧', '🔬', '💊', '⚠️', '🚨', '⏰', '🔍', '👨‍⚕️', '🏥', '😴', '📊', '🩹', '🤢', '🍽️', '🚶‍♂️', '👶', '🍼', '💉', '👪',
    // Rehab department
    '🦾', '🏋️', '🩺', '🛁', '🛡️', '🎯', '📋',
    // Labor & Delivery, OB
    '🤰', '🩸',
    // Oncology, Cardiology, Neurology, Pediatrics
    '🧪', '🧸', '❤️',
    // Additional emojis LLM might use for ADLs/self-care
    '🚽', '🧑‍🤝‍🧑', '👥', '🪥', '👔', '🍴',
    // Psychiatry department
    '🌿', '🗣️', '🗣', '🧘', '💭', '🛏️', '🛏', '🧩'
  ];
  const emojiPattern = new RegExp(`(${emojis.join('|')})`);
  
  const match = text.match(emojiPattern);
  if (match && match.index !== undefined) {
    const narrative = convertMarkdownBold(text.slice(0, match.index).trim());
    const breakdown = text.slice(match.index).trim();
    return { narrative, breakdown };
  }
  
  return { narrative: convertMarkdownBold(text), breakdown: '' };
};

// Helper function to clean up broken span tags from translated text
const cleanBrokenSpanTags = (text: string): string => {
  // Remove broken span tags that got mangled during translation
  // Pattern matches things like: <span class='medication-highlight'>word</span> or medication-highlight">word
  // Also matches partial tags like: -highlight">word or class='vital-sign'>word
  let cleaned = text;
  
  // Remove broken/partial span opening tags (e.g., "medication-highlight">", "class='vital-sign'>")
  cleaned = cleaned.replace(/-highlight">/g, '');
  cleaned = cleaned.replace(/class='[^']*'>/g, '');
  cleaned = cleaned.replace(/<span\s+class='[^']*'>/g, '');
  cleaned = cleaned.replace(/<\/span>/g, '');
  
  // Also clean up any leftover partial patterns
  cleaned = cleaned.replace(/medication-highlight/g, '');
  cleaned = cleaned.replace(/vital-sign/g, '');
  cleaned = cleaned.replace(/high-priority/g, '');
  cleaned = cleaned.replace(/critical-info/g, '');
  
  return cleaned;
};

// Helper function to highlight keywords in handoff text (consistent with audio recorded handoffs)
const highlightKeywords = (text: string): string => {
  // First, clean up any broken span tags from translation
  const cleanedText = cleanBrokenSpanTags(text);
  
  // Enhanced keyword patterns for comprehensive highlighting
  const keywords: Record<string, string> = {
    // Critical safety info - psychiatry and general
    "(confused|confusion|agitated|agitation|delirium|fall risk|high fall risk|bed alarm|restraints|suicide risk|suicidal|SI|HI|homicide risk|homicidal|violence risk|impulsive|impulsive when agitated|elopement risk|self-harm|psychosis|psychotic|manic|mania|paranoid|paranoia|delusions|hallucinations|command hallucinations)":
      "<span class='critical-info'>$1</span>",
    // Vitals with values - match patterns like "HR 98", "BP 124/80", "T 99.0°F", "RR 20", "SpO2 95%"
    "(HR\\s*\\d+|BP\\s*\\d+\\/\\d+|T\\s*\\d+\\.?\\d*°?F?|RR\\s*\\d+|SpO2\\s*\\d+%?|O2\\s*sat\\s*\\d+%?|temp\\s*\\d+\\.?\\d*|pulse\\s*\\d+|heart rate\\s*\\d+)":
      "<span class='vital-sign'>$1</span>",
    // General vital signs terms
    "(low blood pressure|high blood pressure|low bp|bradycardia|tachycardia|spo2|oxygen|abnormal electrolytes|cardiac enzymes|blood counts|glucose levels|blood pressure|vital signs|vitals stable|febrile|afebrile|hypotensive|hypertensive|tachypneic)":
      "<span class='vital-sign'>$1</span>",
    // Pain and priority items
    "(pain|pain level|discomfort|shortness of breath|chest pain|current pain score|PRN given|PRN\\s+\\w+|lab abnormality|pain score|nausea|vomiting|diarrhea|constipation)":
      "<span class='high-priority'>$1</span>",
    // Medications - common psych meds and patterns like "Lithium 900 mg daily"
    "(Lithium\\s*\\d+\\s*mg|Olanzapine\\s*\\d+\\s*mg|Lorazepam\\s*\\d+\\s*mg|Risperidone\\s*\\d+\\s*mg|Quetiapine\\s*\\d+\\s*mg|Haloperidol\\s*\\d+\\s*mg|Clozapine\\s*\\d+\\s*mg|Valproic acid\\s*\\d+\\s*mg|Depakote\\s*\\d+\\s*mg|Lamictal\\s*\\d+\\s*mg|Seroquel\\s*\\d+\\s*mg|Zyprexa\\s*\\d+\\s*mg|Ativan\\s*\\d+\\s*mg|Haldol\\s*\\d+\\s*mg|Klonopin\\s*\\d+\\s*mg|Xanax\\s*\\d+\\s*mg|Prozac\\s*\\d+\\s*mg|Zoloft\\s*\\d+\\s*mg|Lexapro\\s*\\d+\\s*mg|Wellbutrin\\s*\\d+\\s*mg|Effexor\\s*\\d+\\s*mg|Cymbalta\\s*\\d+\\s*mg|Abilify\\s*\\d+\\s*mg|Risperdal\\s*\\d+\\s*mg|Geodon\\s*\\d+\\s*mg|Invega\\s*\\d+\\s*mg|Latuda\\s*\\d+\\s*mg|Vraylar\\s*\\d+\\s*mg)":
      "<span class='medication-highlight'>$1</span>",
    // More medication patterns - general meds with dosing
    "(\\w+\\s*\\d+\\s*mg\\s*(?:daily|BID|TID|QID|PRN|PO|IV|IM|x\\d+|HS|QHS|AM|PM))":
      "<span class='medication-highlight'>$1</span>",
    // General medication terms
    "(medication changes|new orders|held medications|timing changes|adverse reactions|drug allergies|food allergies|latex|medications|PRN given|Scheduled|side effects|tremor|mild tremor|akathisia|EPS|tardive|sedation)":
      "<span class='medication-highlight'>$1</span>",
    // Observation levels - psychiatry specific
    "(Q15 minute checks|Q30 minute checks|Q15 min|Q30 min|1:1 observation|constant observation|line of sight|close observation|elopement precautions|sharps restriction|contraband check)":
      "<span class='high-priority'>$1</span>",
    // I&O and isolation
    "(I&O status|fluid restriction|catheter output|strict I&Os|fluid overload|isolation|contact precautions|droplet precautions|airborne precautions|C-diff|MRSA|intake|output)":
      "<span class='high-priority'>$1</span>",
    // Code status and care planning
    "(code status|full code|DNR|DNI|comfort care only|pending orders|labs due|scheduled procedure|physician call back|discharge planning|family concern|family issues|interpreter needed|emergency contact|stat|urgent|emergency)":
      "<span class='vital-sign'>$1</span>",
    // Behavioral observations - psychiatry
    "(Agitation increases|escalation|de-escalation|limit-setting|redirections|disruptive|cooperative|uncooperative|combative|threatening|verbal aggression|physical aggression|pacing|pressured speech|flight of ideas|tangential|circumstantial|loose associations|thought blocking|flat affect|blunted affect|labile|irritable|elevated mood|depressed mood|anxious|tearful|guarded|paranoid ideation)":
      "<span class='high-priority'>$1</span>",
    // Negative/concerning terms
    "(insufficient|absence|ineffective|lack of|poor|interruption|unsuccessfully|inappropriate|inaccurate|incomplete|unclear|delay|refuse|refused|not done|never|not reported|not notify|not received|not given|not documented|not placed|not completed|no documentation|no orders|no communication|miscommunication|not communicated|not ordered|no IV|not transferred|not released|no sitter|not inform|not followed|not aware|discontinued|not consulted|non-compliant|noncompliant)":
      "<span class='critical-info'>$1</span>"
  };

  // Comprehensive emoji list for all departments
  const emojis = [
    '🫀', '🫁', '🧠', '💧', '🔬', '💊', '⚠️', '🚨', '⏰', '🔍', '👨‍⚕️', '🏥', '😴', '📊', '🩹', '🤢', '🍽️', '🚶‍♂️', '👶', '🍼', '💉', '👪',
    // Rehab department
    '🦾', '🏋️', '🩺', '🛁', '🛡️', '🎯', '📋',
    // Labor & Delivery, OB
    '🤰', '🩸',
    // Oncology, Cardiology, Neurology, Pediatrics
    '🧪', '🧸', '❤️',
    // Additional emojis LLM might use for ADLs/self-care
    '🚽', '🧑‍🤝‍🧑', '👥', '🪥', '👔', '🍴',
    // Psychiatry department
    '🌿', '🗣️', '🗣', '🧘', '💭', '🛏️', '🛏', '🧩'
  ];
  
  // Process line by line to format headers and apply highlights
  const lines = cleanedText.split('\n');
  const processedLines: string[] = [];
  let lastWasHeader = false;
  
  // Helper function to check if a line is a category header
  const isHeaderLine = (line: string): { isHeader: boolean; emoji: string; title: string } => {
    const trimmed = line.trim();
    // Check if line starts with any of our category emojis
    for (const emoji of emojis) {
      if (trimmed.startsWith(emoji)) {
        const rest = trimmed.slice(emoji.length).trim();
        // Header titles are typically ALL CAPS or mixed case like "ADLs & SELF-CARE"
        // They should NOT start with a dash/bullet and should be relatively short
        if (rest && !rest.startsWith('-') && !rest.startsWith('•') && rest.length < 50) {
          // Check if it looks like a header (mostly uppercase letters, spaces, &, -)
          const upperCount = (rest.match(/[A-Z]/g) || []).length;
          const letterCount = (rest.match(/[A-Za-z]/g) || []).length;
          if (letterCount > 0 && upperCount / letterCount > 0.6) {
            return { isHeader: true, emoji, title: rest };
          }
        }
      }
    }
    return { isHeader: false, emoji: '', title: '' };
  };
  
  lines.forEach(line => {
    const trimmedLine = line.trim();
    
    // Skip empty lines that come right after a header
    if (lastWasHeader && trimmedLine === '') {
      lastWasHeader = false;
      return;
    }
    
    // Check if this is a category header line
    const headerCheck = isHeaderLine(trimmedLine);
    if (headerCheck.isHeader) {
      // Format header with bold and larger text
      processedLines.push(`<span class="category-header">${headerCheck.emoji} ${headerCheck.title}</span>`);
      lastWasHeader = true;
      return;
    }
    
    lastWasHeader = false;
    
    // Apply highlighting to non-header lines
    let highlightedLine = line;
    Object.entries(keywords).forEach(([pattern, replace]) => {
      const regex = new RegExp(`\\b${pattern}\\b`, 'gi');
      highlightedLine = highlightedLine.replace(regex, replace);
    });
    processedLines.push(highlightedLine);
  });
  
  return processedLines.join('\n');
};

// Type for dynamic highlights
type DynamicHighlights = {
  'critical-info': string[];
  'vital-sign': string[];
  'high-priority': string[];
  'medication-highlight': string[];
};

// Function to apply dynamic highlights to text
const applyDynamicHighlights = (text: string, highlights: DynamicHighlights | null): string => {
  if (!highlights) return text;
  
  // Comprehensive emoji list for header detection
  const emojis = [
    '🫀', '🫁', '🧠', '💧', '🔬', '💊', '⚠️', '🚨', '⏰', '🔍', '👨‍⚕️', '🏥', '😴', '📊', '🩹', '🤢', '🍽️', '🚶‍♂️', '👶', '🍼', '💉', '👪',
    '🦾', '🏋️', '🩺', '🛁', '🛡️', '🎯', '📋', '🤰', '🩸', '🧪', '🧸', '❤️', '🚽', '🧑‍🤝‍🧑', '👥', '🪥', '👔', '🍴',
    '🌿', '🗣️', '🗣', '🧘', '💭', '🛏️', '🛏', '🧩'
  ];
  
  // Helper function to check if a line is a category header
  const isHeaderLine = (line: string): { isHeader: boolean; emoji: string; title: string } => {
    const trimmed = line.trim();
    for (const emoji of emojis) {
      if (trimmed.startsWith(emoji)) {
        const rest = trimmed.slice(emoji.length).trim();
        if (rest && !rest.startsWith('-') && !rest.startsWith('•') && rest.length < 50) {
          const upperCount = (rest.match(/[A-Z]/g) || []).length;
          const letterCount = (rest.match(/[A-Za-z]/g) || []).length;
          if (letterCount > 0 && upperCount / letterCount > 0.6) {
            return { isHeader: true, emoji, title: rest };
          }
        }
      }
    }
    return { isHeader: false, emoji: '', title: '' };
  };
  
  // Process line by line
  const lines = text.split('\n');
  const processedLines = lines.map(line => {
    // Check if this is a category header line
    const headerCheck = isHeaderLine(line);
    if (headerCheck.isHeader) {
      // Format header with bold and larger text using category-header class
      return `<span class="category-header">${headerCheck.emoji} ${headerCheck.title}</span>`;
    }
    
    let highlightedLine = line;
    
    // Apply highlights for each category (longer phrases first to avoid partial matches)
    const categories: Array<keyof DynamicHighlights> = 
      ['critical-info', 'vital-sign', 'high-priority', 'medication-highlight'];
    
    categories.forEach(category => {
      const terms = highlights[category] || [];
      // Sort by length descending to match longer phrases first
      const sortedTerms = [...terms].sort((a, b) => b.length - a.length);
      
      sortedTerms.forEach(term => {
        if (term && term.length > 1) {
          // Escape special regex characters in the term
          const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
          // Use word boundaries but be flexible for phrases
          const regex = new RegExp(`(?<!<span[^>]*>)\\b(${escapedTerm})\\b(?![^<]*<\\/span>)`, 'gi');
          highlightedLine = highlightedLine.replace(regex, `<span class='${category}'>$1</span>`);
        }
      });
    });
    
    return highlightedLine;
  });
  
  return processedLines.join('\n');
};

// Collapsible handoff card component
const HandoffCard: React.FC<{
  handoff: Handoff;
  title: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  onToggle: () => void;
  department?: string;
}> = ({ handoff, title, icon, isExpanded, onToggle, department }) => {
  const { theme } = useTheme();
  
  // Parse content immediately for initial state
  const { narrative: initialNarrative, breakdown: initialBreakdown } = splitHandoffContent(handoff.handoff_text);
  
  const [narrativeExpanded, setNarrativeExpanded] = useState(true);
  const [breakdownExpanded, setBreakdownExpanded] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en');
  const [isTranslating, setIsTranslating] = useState(false);
  const [displayedNarrative, setDisplayedNarrative] = useState<string>(initialNarrative);
  const [displayedBreakdown, setDisplayedBreakdown] = useState<string>(initialBreakdown);
  const [originalNarrative, setOriginalNarrative] = useState<string>(initialNarrative);
  const [originalBreakdown, setOriginalBreakdown] = useState<string>(initialBreakdown);
  const [dynamicHighlights, setDynamicHighlights] = useState<DynamicHighlights | null>(null);
  const [isLoadingHighlights, setIsLoadingHighlights] = useState(false);
  const [highlightsLoaded, setHighlightsLoaded] = useState(false);
  
  const formatTimestamp = (timestamp: string) => {
    try {
      // Timestamps from backend are already in Central Time - display as-is without timezone conversion
      const date = new Date(timestamp);
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return timestamp;
    }
  };

  const hasBreakdown = initialBreakdown.length > 0;

  // Update content if handoff text changes
  useEffect(() => {
    const { narrative, breakdown } = splitHandoffContent(handoff.handoff_text);
    setDisplayedNarrative(narrative);
    setDisplayedBreakdown(breakdown);
    setOriginalNarrative(narrative);
    setOriginalBreakdown(breakdown);
    // Reset highlights state when handoff changes
    setDynamicHighlights(null);
    setHighlightsLoaded(false);
  }, [handoff.handoff_text]);

  // Fetch dynamic highlights from LLM when breakdown is expanded
  const fetchDynamicHighlights = useCallback(async () => {
    if (highlightsLoaded || isLoadingHighlights || !originalBreakdown) return;
    
    setIsLoadingHighlights(true);
    try {
      const response = await fetch(`${BACKEND_URL}/handoff/extract-highlights`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: handoff.handoff_text,
          department: department || 'General'
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.highlights) {
        setDynamicHighlights(data.highlights);
        // Apply dynamic highlights to the displayed content
        const highlightedNarrative = applyDynamicHighlights(originalNarrative, data.highlights);
        const highlightedBreakdown = applyDynamicHighlights(originalBreakdown, data.highlights);
        setDisplayedNarrative(highlightedNarrative);
        setDisplayedBreakdown(highlightedBreakdown);
      }
    } catch (error) {
      console.error('Error fetching dynamic highlights:', error);
      // Fall back to static highlighting
      setDisplayedNarrative(highlightKeywords(originalNarrative));
      setDisplayedBreakdown(highlightKeywords(originalBreakdown));
    } finally {
      setIsLoadingHighlights(false);
      setHighlightsLoaded(true);
    }
  }, [department, handoff.handoff_text, highlightsLoaded, isLoadingHighlights, originalNarrative, originalBreakdown]);

  // Fetch highlights when card is expanded
  useEffect(() => {
    if (isExpanded && !highlightsLoaded && !isLoadingHighlights) {
      fetchDynamicHighlights();
    }
  }, [isExpanded, highlightsLoaded, isLoadingHighlights, fetchDynamicHighlights]);

  // Handle language change and translation
  const handleLanguageChange = useCallback(async (langCode: string) => {
    setSelectedLanguage(langCode);
    
    // If switching back to English, restore original content
    if (langCode === 'en') {
      setDisplayedNarrative(originalNarrative);
      setDisplayedBreakdown(originalBreakdown);
      return;
    }
    
    setIsTranslating(true);
    
    try {
      const apiKey = process.env.REACT_APP_OPENAI_API_KEY;
      if (!apiKey) {
        console.error('OpenAI API key required for translation');
        setIsTranslating(false);
        setSelectedLanguage('en');
        return;
      }
      
      // Translate narrative
      const narrativeResponse = await fetch(`${BACKEND_URL}/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: originalNarrative,
          target_language: langCode,
          api_key: apiKey
        })
      });
      
      const narrativeData = await narrativeResponse.json();
      
      if (!narrativeData.success) {
        throw new Error(narrativeData.message || 'Translation failed');
      }
      
      setDisplayedNarrative(narrativeData.translated_text);
      
      // Translate breakdown if it exists
      if (originalBreakdown) {
        const breakdownResponse = await fetch(`${BACKEND_URL}/translate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            text: originalBreakdown,
            target_language: langCode,
            api_key: apiKey
          })
        });
        
        const breakdownData = await breakdownResponse.json();
        
        if (breakdownData.success) {
          setDisplayedBreakdown(breakdownData.translated_text);
        }
      }
      
    } catch (error) {
      console.error('Translation error:', error);
      setSelectedLanguage('en');
      setDisplayedNarrative(originalNarrative);
      setDisplayedBreakdown(originalBreakdown);
    } finally {
      setIsTranslating(false);
    }
  }, [originalNarrative, originalBreakdown]);

  const textColor = theme === 'dark' ? '#f9fafb' : '#1f2937';

  return (
    <div className={`previous-handoff-container ${isExpanded ? 'expanded' : ''}`}>
      <button 
        className="previous-handoff-header"
        onClick={onToggle}
        aria-expanded={isExpanded}
      >
        <div className="handoff-header-left">
          {icon}
          <div className="handoff-header-info">
            <span className="handoff-header-title">{title}</span>
            <span className="handoff-header-meta">
              From {handoff.nurse_name}
            </span>
          </div>
        </div>
        <div className="handoff-header-right">
          {isLoadingHighlights && (
            <>
              <span className="highlighting-text">Highlighting critical info...</span>
              <Loader2 size={16} className="spin" style={{ marginRight: '8px' }} />
            </>
          )}
          {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </div>
      </button>
      
      {isExpanded && (
        <div className="previous-handoff-content">
          <div className="handoff-meta-row">
            <div className="handoff-meta-item">
              <User size={14} />
              <span>{handoff.nurse_name}</span>
            </div>
            <div className="handoff-meta-item">
              <Clock size={14} />
              <span>{formatTimestamp(handoff.timestamp)}</span>
            </div>
          </div>
          
          {/* Language Selector */}
          <div className="language-selector-row">
            <Globe size={14} className="language-icon" />
            <span className="language-label">Display in:</span>
            <select
              value={selectedLanguage}
              onChange={(e) => handleLanguageChange(e.target.value)}
              disabled={isTranslating}
              className="language-select"
              style={{
                backgroundColor: theme === 'dark' ? '#1e293b' : '#ffffff',
                color: textColor,
                borderColor: theme === 'dark' ? '#374151' : '#d1d5db'
              }}
            >
              {Object.entries(SUPPORTED_LANGUAGES).map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
            {isTranslating && (
              <Loader2 size={14} className="translating-spinner" />
            )}
          </div>

          {/* Narrative Section - Collapsible */}
          <div className="handoff-section">
            <button 
              className="handoff-section-header"
              onClick={() => setNarrativeExpanded(!narrativeExpanded)}
            >
              <span>Narrative</span>
              {narrativeExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {narrativeExpanded && (
              <div 
                className="handoff-text"
                dangerouslySetInnerHTML={{ 
                  __html: dynamicHighlights 
                    ? applyDynamicHighlights(displayedNarrative, dynamicHighlights)
                    : displayedNarrative 
                }}
              />
            )}
          </div>
          
          {/* Categorical Breakdown Section - Collapsible */}
          {hasBreakdown && (
            <div className="handoff-section">
              <button 
                className="handoff-section-header"
                onClick={() => setBreakdownExpanded(!breakdownExpanded)}
              >
                <span>Categorical Breakdown</span>
                {breakdownExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </button>
              {breakdownExpanded && (
                <div 
                  className="handoff-text breakdown-text"
                  dangerouslySetInnerHTML={{ 
                    __html: dynamicHighlights 
                      ? applyDynamicHighlights(displayedBreakdown, dynamicHighlights)
                      : highlightKeywords(displayedBreakdown) 
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PreviousHandoff: React.FC<PreviousHandoffProps> = ({ patientMRN, currentNurseId, department }) => {
  const [audioHandoff, setAudioHandoff] = useState<Handoff | null>(null);
  const [generatedHandoff, setGeneratedHandoff] = useState<Handoff | null>(null);
  const [audioExpanded, setAudioExpanded] = useState(false);
  const [generatedExpanded, setGeneratedExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchHandoffs = async () => {
      if (!patientMRN) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        
        const response = await fetch(
          `${BACKEND_URL}/handoff/latest-by-type/${patientMRN}?exclude_nurse_id=${encodeURIComponent(currentNurseId)}`
        );
        
        const data = await response.json();
        
        if (data.success) {
          setAudioHandoff(data.audio_handoff || null);
          setGeneratedHandoff(data.generated_handoff || null);
        } else {
          setAudioHandoff(null);
          setGeneratedHandoff(null);
        }
      } catch (err) {
        console.error('Error fetching previous handoffs:', err);
        setError('Failed to load previous handoffs');
        setAudioHandoff(null);
        setGeneratedHandoff(null);
      } finally {
        setLoading(false);
      }
    };

    fetchHandoffs();
  }, [patientMRN, currentNurseId]);

  // Show loading state
  if (loading) {
    return null;
  }

  // Check if we have any handoffs
  const hasAudio = audioHandoff !== null;
  const hasGenerated = generatedHandoff !== null;
  const hasBoth = hasAudio && hasGenerated;

  // Show "no previous handoff" state
  if (error || (!hasAudio && !hasGenerated)) {
    return (
      <div className="previous-handoff-container no-handoff">
        <div className="no-handoff-content">
          <FileText size={20} className="handoff-icon" />
          <span className="no-handoff-text">No previous handoff for this patient</span>
        </div>
      </div>
    );
  }

  return (
    <div className="previous-handoffs-wrapper">
      {/* Generated Handoff */}
      {hasGenerated && (
        <HandoffCard
          handoff={generatedHandoff}
          title={hasBoth ? "Previous Shift Generated Handoff" : "Previous Shift Handoff"}
          icon={<FileText size={20} className="handoff-icon" />}
          isExpanded={generatedExpanded}
          onToggle={() => setGeneratedExpanded(!generatedExpanded)}
          department={department}
        />
      )}
      
      {/* Audio Handoff */}
      {hasAudio && (
        <HandoffCard
          handoff={audioHandoff}
          title={hasBoth ? "Previous Shift Audio Handoff" : "Previous Shift Handoff"}
          icon={<Mic size={20} className="handoff-icon" />}
          isExpanded={audioExpanded}
          onToggle={() => setAudioExpanded(!audioExpanded)}
          department={department}
        />
      )}
    </div>
  );
};

export default PreviousHandoff;
